/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { flatMap as _flatmap } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions } from '@amfam/billing/one-time-pay/feature';
import { pendingPaymentQuery } from '@amfam/billing/pending-payment/data-access';
import { BillAccount, BillingPaymentPaths } from '@amfam/shared/models';
import { ArrayToList, CurrencyUSDPipe, PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  Option,
  ValidationService
} from '@amfam/ui-kit';

import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';

@Component({
  selector: 'amfam-choose-account-wrapper',
  templateUrl: './choose-account-wrapper.component.html',
  styleUrls: ['./choose-account-wrapper.component.scss'],
  standalone: false
})
export class ChooseAccountWrapperComponent implements OnInit, OnDestroy {
  billAccountSelectionFormGroup!: UntypedFormGroup;
  billAccountOptions$!: Observable<Option[]>;
  selectedBillAccountNumbers: string[] = [];
  showAutoPaySetUpLink$: Observable<boolean> = this.oneTimePayFacade.showAutoPaySetUpLink();
  cancelModalConfig: AlertModalConfig = this.oneTimePayFacade.cancelModalConfig();

  silentRegistrationAllFailedError: AlertModalConfig =
    this.oneTimePayFacade.silentRegistrationAllFailedError();

  silentRegistrationPartialError: AlertModalConfig =
    this.oneTimePayFacade.silentRegistrationPartialError();
  linkEventText: string;
  isMyAccountAdmin = this.appService.isApp(Applications.MYACCOUNT_ADMIN);
  setUpAutoPaySubHeader: string;
  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private modalService: DsModalService,
    private copyService: CopyService,
    private dockingBar: DockingBarService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private spinnerService: LoadingSpinnerService,
    private appService: ApplicationService
  ) {}

  ngOnInit(): void {
    this.spinnerService.stop();
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.oneTimePayRefactor.dockingBarHeading')
    );
    this.store
      .select(routerQuery.getRouterPreviousStateUrl)
      .pipe(take(1))
      .subscribe(url => this.store.dispatch(OneTimePayActions.setOneTimePayCancelPath({ url })));
    this.setUpAutoPaySubHeader = this.isMyAccountAdmin
      ? this.copyService.getCopy(
          'billing.oneTimePayRefactor.myAccountAdmin.setUpAutoPaySubHeaderAdmin'
        )
      : this.copyService.getCopy('billing.oneTimePayRefactor.setUpAutoPaySubHeader');
    this.store
      .select(pendingPaymentQuery.selectPendingPaymentIds)
      .pipe(take(1))
      .subscribe(preselectedAccounts => {
        this.getForm(preselectedAccounts);
        this.selectedBillAccountNumbers = preselectedAccounts;
        this.handleCheckBoxInteraction();
      });
  }

  getForm(preselectedBillAccountNumbers: string[]): void {
    this.billAccountOptions$ = combineLatest([
      this.store.select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
    ]).pipe(
      take(1),
      map(([billAccountsEligibleForOneTimePay]) =>
        billAccountsEligibleForOneTimePay.map(billAccount => {
          const option: Option = {
            text:
              new ArrayToList().transform(
                _flatmap(billAccount.policyList, policy => policy.riskDescriptionList)
              ) || billAccount?.billingPreferences?.accountNickName,
            helpText: [
              {
                text: `Acct ${new PrettyBillingAcctNum().transform(billAccount.billAccountNumber)}`
              }
            ],
            id: billAccount.billAccountNumber,
            pastDue: billAccount.pastDue,
            isSelected: preselectedBillAccountNumbers.includes(billAccount.billAccountNumber),
            isAdvance: billAccount.isAdvance
          };
          if (billAccount.pastDue) {
            option.helpText.push({
              text: `${new CurrencyUSDPipe().transform(billAccount.minimumAmountDue)} due Today`,
              warning: true
            });
          } else {
            if (Number(billAccount.minimumAmountDue) === 0) {
              option.helpText.push({
                text: `${new CurrencyUSDPipe().transform(billAccount.minimumAmountDue)} due`
              });
            } else {
              option.helpText.push({
                text: `${new CurrencyUSDPipe().transform(
                  billAccount.minimumAmountDue
                )} due ${format(billAccount.paymentDueDate, 'MMM DD')}`
              });
            }
          }
          return option;
        })
      )
    );

    this.billAccountOptions$
      .pipe(takeUntil(this.stop$))
      .subscribe((billAccountOptions: Option[]) => {
        const formControls = billAccountOptions.map(
          billAccountOption =>
            new UntypedFormControl({
              value: billAccountOption.isSelected,
              disabled: billAccountOption.disabled
            })
        );
        this.billAccountSelectionFormGroup = this.formBuilder.group(
          {
            option: new UntypedFormArray(formControls)
          },
          { validators: ValidationService.minimumCheckedCheckboxCheckedValidator() }
        );
      });
  }

  handleCheckBoxInteraction() {
    this.selectedBillAccountNumbers = [];
    this.billAccountOptions$.pipe(take(1)).subscribe(billAccountOptions => {
      (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls.forEach(
        (option, i) => {
          billAccountOptions[i].isSelected = option.value;
        }
      );

      const hasSelectedClassicPastDue =
        billAccountOptions.filter(ba => ba.isSelected && ba.pastDue && !ba.isAdvance).length > 0;
      const hasSelectedCurrent =
        billAccountOptions.filter(ba => ba.isSelected && !ba.pastDue).length > 0;
      const hasSelectedAdvancedPastDue =
        billAccountOptions.filter(ba => ba.isSelected && ba.pastDue && ba.isAdvance).length > 0;

      billAccountOptions.forEach((billAccount, i) => {
        if (hasSelectedClassicPastDue) {
          if (billAccount.pastDue && !billAccount.isAdvance) {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].enable();
          } else {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].disable();
          }
        } else if (hasSelectedCurrent) {
          if (!billAccount.pastDue) {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].enable();
          } else {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].disable();
          }
        } else if (hasSelectedAdvancedPastDue) {
          if (billAccount.pastDue && billAccount.isAdvance) {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].enable();
          } else {
            (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[
              i
            ].disable();
          }
        } else {
          (<UntypedFormArray>this.billAccountSelectionFormGroup.get('option')).controls[i].enable();
        }

        if (billAccount.isSelected) {
          this.selectedBillAccountNumbers.push(billAccount.id.toString());
        }
      });
    });
  }

  goToSetUpAutoPayFlow() {
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'ChooseAccount:SetupAutoPay' });
    this.modalService.open('oneTimePayCancelModal');
    this.linkEventText = this.copyService.getCopy(
      `billing.oneTimePayRefactor.alertMessages.setupAutopayText`
    );
  }

  submit() {
    let selectedBillAccounts: BillAccount[];

    this.store
      .select(BillAccountsSelectors.selectAllBillAccounts)
      .pipe(takeUntil(this.stop$))
      .subscribe(
        billAccounts =>
          (selectedBillAccounts = billAccounts.filter(billAccount =>
            this.selectedBillAccountNumbers.includes(billAccount.billAccountNumber)
          ))
      );
    this.oneTimePayFacade.registerBillAccountsBeforeOneTimePay(selectedBillAccounts);
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'ChooseAccount:SetupAutoPay' });
  }

  cancel() {
    this.modalService.open('oneTimePayCancelModal');
  }

  stay() {
    this.modalService.close(this.cancelModalConfig.modalId);
  }

  leave() {
    this.modalService.closeAll();
    this.store.dispatch(
      OneTimePayActions.leaveOTPFlow({
        billAccountNumber: '',
        id: this.linkEventText
      })
    );
  }

  closeSilentRegistrationErrorModal() {
    this.modalService.closeAll();
  }

  continueWithSuccessfulRegistration() {
    this.modalService.close('silentRegistrationErrorModalSomeFailed');
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
